import { WayNormalizedPurchase } from '../models/way-purchase.models';
import { formatCheckoutCurrency } from '../../notification-modal/utils/notification-currency.util';

export const WAY_PURCHASE_LANE_SUBTITLE =
  'Recognized at the lane via license plate. Review and confirm the order.';

/** True when Way modal was opened from order-request plate/phone/email lookup (not lane LPR). */
export function isOrderRequestLookupSource(
  data: Record<string, any> | null | undefined
): boolean {
  return String(readRecord(data)?.['source'] ?? '').trim() === 'order_request_lookup';
}

export function resolveWayPurchaseSubtitle(
  data: Record<string, any> | null | undefined
): string {
  if (isOrderRequestLookupSource(data)) {
    return '';
  }
  return WAY_PURCHASE_LANE_SUBTITLE;
}

export function readRecord(value: any): Record<string, any> | null {
  return value != null && typeof value === 'object' ? (value as Record<string, any>) : null;
}

export function pickString(...values: any[]): string {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
      return String(value);
    }
  }
  return '';
}

export function readPurchase(
  data: Record<string, any> | null | undefined
): WayNormalizedPurchase | null {
  const row = readRecord(data);
  if (!row) {
    return null;
  }
  const purchase = readRecord(row['purchase']);
  return purchase ? (purchase as WayNormalizedPurchase) : null;
}

export function formatLongDate(value?: string | null): string {
  if (!value?.trim()) {
    return '—';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export function formatPrice(value: any): string {
  if (value == null || value === '') {
    return '—';
  }
  const numeric = typeof value === 'number' ? value : Number.parseFloat(String(value));
  if (!Number.isFinite(numeric)) {
    return '—';
  }
  return formatCheckoutCurrency(numeric);
}

export function formatOrderId(wayPassId: string): string {
  if (!wayPassId) {
    return '—';
  }
  return wayPassId.startsWith('#') ? wayPassId : `#${wayPassId}`;
}

export function resolveLicensePlate(
  _row: Record<string, any> | null,
  purchase: WayNormalizedPurchase | null
): string {
  // Show exactly what Way returned — do not prefer LPR CA-prefixed plate/plateNumber.
  return pickString(purchase?.plate_number) || '—';
}

export function formatWashesAvailable(
  washesIncluded: number | null | undefined,
  washesRedeemed: number | null | undefined = 0,
  washesRemaining: number | null | undefined = undefined
): string {
  if (washesRemaining != null) {
    return String(Math.max(0, washesRemaining));
  }
  if (washesIncluded == null) {
    return 'Unlimited';
  }
  const redeemed = washesRedeemed ?? 0;
  return String(Math.max(0, washesIncluded - redeemed));
}

export function formatWashesRemaining(washesRemaining: number | null | undefined): string {
  if (washesRemaining == null) {
    return 'Unlimited';
  }
  return String(Math.max(0, washesRemaining));
}

export function formatWashesRedeemed(washesRedeemed: number | null | undefined): string {
  if (washesRedeemed == null) {
    return '0';
  }
  return String(washesRedeemed);
}

/** Total plan allowance for active-subscription display (not remaining). */
export function formatTotalWashesAvailable(washesIncluded: number | null | undefined): string {
  if (washesIncluded == null) {
    return 'Unlimited';
  }
  return String(washesIncluded);
}

/** Matches local active-subscription modal: N/A when missing, else long US date. */
export function formatActiveSubscriptionLastWashedDate(value?: string | null): string {
  if (!value?.trim()) {
    return 'N/A';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export function formatShortDate(value?: string | null): string {
  if (!value?.trim()) {
    return '—';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function formatPurchasedWithSource(
  value?: string | null,
  sourceLabel: 'Shop' | 'Way' = 'Shop'
): string {
  const dateLabel = formatShortDate(value);
  if (dateLabel === '—') {
    return `— · ${sourceLabel}`;
  }
  return `${dateLabel} · ${sourceLabel}`;
}
